/**
 * AdMockupHeader Component
 *
 * Standardized header for social media ad mockups
 * Adapts styling based on platform
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { AdMockupHeaderProps } from '../types/base.types';

export const AdMockupHeader: React.FC<AdMockupHeaderProps> = ({
  brandName,
  brandLogo,
  platform,
  sponsored = true,
  verified = false,
  timestamp = 'Sponsored',
}) => {
  // Platform-specific styling
  const platformStyles = {
    facebook: 'p-4',
    meta: 'p-4',
    instagram: 'p-3',
    tiktok: 'p-3',
    linkedin: 'p-4',
    google_search: 'p-0',
    google_display: 'p-0',
    google_shopping: 'p-0',
    youtube: 'p-0',
    pinterest: 'p-3',
    twitter: 'p-3',
  };

  const padding = platformStyles[platform] || 'p-4';

  return (
    <div className={cn('flex items-center gap-3', padding)}>
      {/* Brand Logo/Avatar */}
      <div className="flex-shrink-0">
        {brandLogo ? (
          <img
            src={brandLogo}
            alt={brandName}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold text-sm">
            {brandName.slice(0, 2).toUpperCase()}
          </div>
        )}
      </div>

      {/* Brand Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
            {brandName}
          </p>
          {verified && (
            <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          {sponsored && <span>{timestamp}</span>}
          {platform === 'facebook' && (
            <>
              <span>•</span>
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
              </svg>
            </>
          )}
        </div>
      </div>

      {/* Options Menu */}
      <button className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
    </div>
  );
};

export default AdMockupHeader;
